/**
 * Compact row of icons summarising the clients connected to a smartbox's
 * Peplink. One icon per client:
 *
 *   🔌 — name === "Hubspace" (the smart plug; trumps connection type)
 *   📶 — wireless / wifi
 *   🔗 — hardwired (ethernet / lan_*)
 *
 * Each icon's `title` attribute holds the client's name + ip, so hovering
 * surfaces the device. Capped at `max` icons; the rest are summarised
 * as a `+N` chip whose tooltip lists the overflow names.
 *
 * Used by:
 *   - MapPopup smartplug section (max ~8, condensed)
 *   - SignalDetailPage Peplink Clients tile header (max ~24)
 */
import React from "react";

import type { PeplinkClient } from "../../types/boxes";

export interface PeplinkClientIconsProps {
  clients: PeplinkClient[];
  /** Cap visible icons; the rest become a `+N` chip. */
  max?: number;
}

/** True when the client is the Hubspace smart plug. */
function isHubspace(c: PeplinkClient): boolean {
  return (c.name ?? "").trim().toLowerCase() === "hubspace";
}

function iconFor(c: PeplinkClient): string {
  if (isHubspace(c)) return "🔌";
  if (c.is_hardwired) return "🔗";
  return "📶";
}

function tooltipFor(c: PeplinkClient): string {
  const name = c.name?.trim() || c.mac;
  const kind = isHubspace(c)
    ? "Hubspace plug"
    : c.is_hardwired
      ? "hardwired"
      : "wifi";
  return c.ip ? `${name} — ${kind} (${c.ip})` : `${name} — ${kind}`;
}

export const PeplinkClientIcons: React.FC<PeplinkClientIconsProps> = ({
  clients,
  max = 8,
}) => {
  if (clients.length === 0) return null;
  const shown = clients.slice(0, max);
  const overflow = clients.length - shown.length;
  const overflowTitle =
    overflow > 0
      ? clients
          .slice(max)
          .map((c) => c.name?.trim() || c.mac)
          .join(", ")
      : "";

  return (
    <span className="inline-flex items-center gap-0.5 flex-wrap">
      {shown.map((c, i) => (
        <span
          key={`${c.mac}-${i}`}
          title={tooltipFor(c)}
          className="cursor-default leading-none select-none"
          aria-label={tooltipFor(c)}
        >
          {iconFor(c)}
        </span>
      ))}
      {overflow > 0 && (
        <span
          title={overflowTitle}
          className="ml-0.5 px-1 py-0.5 rounded bg-secondary-100 text-secondary-700 text-[10px] font-semibold"
        >
          +{overflow}
        </span>
      )}
    </span>
  );
};

export default PeplinkClientIcons;
